/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from '../../shared/modals/Modal';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, Text, TextInput, TouchableOpacity, View, useWindowDimensions } from 'react-native';
import { ModalNestedPicker } from '../../shared/modals/ModalNestedPicker'
import { modalExperience } from '../../shared/modals/modalExperience.styles'
import { ModalScreenHeader } from '../../shared/modals/ModalScreenHeader';
import { listVariantsForProduct } from '../../catalog/infra-api'
import { searchProducts } from '../../catalog/infra-api';
import type { ProductSearchRow } from '../../catalog/domain-api'
import type { ProductVariant } from '../../catalog/domain-api';
import { formatMoney } from '../../shared/format/public-api';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { CONTENT_WIDE_BREAKPOINT } from '../../shared/layout/breakpoints';

type Props = {
  visible: boolean;
  onClose: () => void;
  /** #714: onPick ahora recibe múltiples variantes a la vez. */
  onPick: (inputs: Array<{ productId: string; variantId: string; label: string }>) => void;
};

function variantLabel(variant: ProductVariant): string {
  const suffix = variant.nameSuffix?.trim();
  if (suffix) return suffix;
  const attrs = Object.entries(variant.attributes ?? {});
  if (attrs.length > 0) {
    return attrs.map(([, v]) => v).join(' · ');
  }
  return 'Variante';
}

function variantDetail(variant: ProductVariant): string {
  const parts: string[] = [];
  if (variant.sku?.trim()) parts.push(`SKU: ${variant.sku.trim()}`);
  if (variant.price != null) {
    const price = Number(variant.price);
    if (Number.isFinite(price)) {
      parts.push(formatMoney(price));
    }
  }
  return parts.join(' · ');
}

/**
 * Picker en dos pasos para target de promo «Variante»:
 * 1) elegir producto (con búsqueda),
 * 2) elegir una o más variantes activas de ese producto (selección masiva).
 */
export function PromotionVariantPickerModal({ visible, onClose, onPick }: Props) {
  const [step, setStep] = useState<'product' | 'variant'>('product');
  const [productOptions, setProductOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [variants, setVariants] = useState<ProductVariant[]>([]);
  const [variantsLoading, setVariantsLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<{ id: string; name: string } | null>(null);
  // #714: selección múltiple
  const [selectedVariantIds, setSelectedVariantIds] = useState<Set<string>>(new Set());
  // #714: búsqueda de producto
  const [productSearch, setProductSearch] = useState('');
  const { width: screenWidth } = useWindowDimensions();
  const isMobile = screenWidth < CONTENT_WIDE_BREAKPOINT;

  useEffect(() => {
    if (!visible) {
      setStep('product');
      setProductOptions([]);
      setVariants([]);
      setSelectedProduct(null);
      setVariantsLoading(false);
      setSelectedVariantIds(new Set());
      setProductSearch('');
    }
  }, [visible]);

  useEffect(() => {
    if (!visible || step !== 'product') return;
    let cancelled = false;
    void (async () => {
      try {
        const rows = (await searchProducts({ limit: 200 })) as ProductSearchRow[];
        if (cancelled) return;
        setProductOptions(
          rows.map((row) => ({
            value: row.id,
            label: row.name,
          }))
        );
      } catch {
        if (!cancelled) setProductOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, step]);

  // #714: filtrar productos por búsqueda
  const filteredProductOptions = useMemo(() => {
    if (!productSearch.trim()) return productOptions;
    const q = productSearch.toLowerCase().trim();
    return productOptions.filter((p) => p.label.toLowerCase().includes(q));
  }, [productOptions, productSearch]);

  useEffect(() => {
    if (!visible || step !== 'variant' || !selectedProduct) return;
    let cancelled = false;
    setVariantsLoading(true);
    void (async () => {
      try {
        const result = await listVariantsForProduct(selectedProduct.id);
        if (cancelled) return;
        const active = result.filter((v) => v.isActive);
        setVariants(active);
      } catch {
        if (!cancelled) setVariants([]);
      } finally {
        if (!cancelled) setVariantsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, step, selectedProduct]);

  const toggleVariant = (variantId: string) => {
    setSelectedVariantIds((prev) => {
      const next = new Set(prev);
      if (next.has(variantId)) next.delete(variantId);
      else next.add(variantId);
      return next;
    });
  };

  const selectAllVariants = () => {
    setSelectedVariantIds(new Set(variants.map((v) => v.id)));
  };

  const clearSelection = () => {
    setSelectedVariantIds(new Set());
  };

  const handleConfirm = () => {
    if (!selectedProduct || selectedVariantIds.size === 0) return;
    const picks = variants
      .filter((v) => selectedVariantIds.has(v.id))
      .map((v) => ({
        productId: selectedProduct.id,
        variantId: v.id,
        label: `${selectedProduct.name} (${variantLabel(v)})`,
      }));
    onPick(picks);
    onClose();
  };

  const title = step === 'product' ? 'Elegir producto' : 'Elegir variantes';
  const subtitle =
    step === 'product'
      ? 'Primero el producto, después las variantes.'
      : 'Seleccioná una o más variantes. La promo vale solo para las elegidas.';

  if (step === 'product') {
    return (
      <ModalNestedPicker
        visible={visible}
        title={title}
        options={filteredProductOptions}
        selectedValue=""
        onSelect={(productId) => {
          const product = productOptions.find((p) => p.value === productId);
          setSelectedProduct({ id: productId, name: product?.label ?? productId });
          setStep('variant');
        }}
        onClose={onClose}
      />
    );
  }

  // #714: step de variantes con selección múltiple, SKU/precio y botón "Todas".

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={[styles.sheet, isMobile ? styles.sheetMobile : styles.sheetDesktop]}>
          <ModalScreenHeader title={title} subtitle={subtitle} onClose={onClose} />
          <View style={styles.variantHeader}>
            <View style={styles.variantActions}>
              <TouchableOpacity onPress={selectAllVariants} disabled={variants.length === 0}>
                <Text style={styles.actionLink}>Todas</Text>
              </TouchableOpacity>
              <Text style={styles.actionSep}>·</Text>
              <TouchableOpacity onPress={clearSelection} disabled={selectedVariantIds.size === 0}>
                <Text style={styles.actionLink}>Limpiar</Text>
              </TouchableOpacity>
              <Text style={styles.selectedCount}>
                {selectedVariantIds.size} seleccionada{selectedVariantIds.size === 1 ? '' : 's'}
              </Text>
            </View>
            <TouchableOpacity
              onPress={handleConfirm}
              disabled={selectedVariantIds.size === 0}
              style={[styles.confirmBtn, selectedVariantIds.size === 0 && styles.confirmBtnDisabled]}
            >
              <Text style={styles.confirmBtnText}>Agregar</Text>
            </TouchableOpacity>
          </View>
          <ScrollView style={styles.variantList}>
            {variantsLoading ? (
              <View style={{ padding: 24, alignItems: 'center' }}>
                <ActivityIndicator />
              </View>
            ) : variants.length === 0 ? (
              <Text style={styles.emptyText}>Este producto no tiene variantes activas.</Text>
            ) : (
              variants.map((variant) => {
                const isSelected = selectedVariantIds.has(variant.id);
                const detail = variantDetail(variant);
                return (
                  <Pressable
                    key={variant.id}
                    onPress={() => toggleVariant(variant.id)}
                    style={[styles.variantRow, isSelected && styles.variantRowSelected]}
                  >
                    <View style={styles.checkbox}>
                      {isSelected && <Text style={styles.checkboxText}>✓</Text>}
                    </View>
                    <View style={styles.variantInfo}>
                      <Text style={styles.variantLabel} numberOfLines={1}>
                        {variantLabel(variant)}
                      </Text>
                      {detail ? (
                        <Text style={styles.variantDetail} numberOfLines={1}>
                          {detail}
                        </Text>
                      ) : null}
                    </View>
                  </Pressable>
                );
              })
            )}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(0,0,0,0.4)',
  },
  sheet: {
    backgroundColor: ticketColors.paper,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '85%',
  },
  sheetMobile: {
    width: '100%',
  },
  sheetDesktop: {
    width: 480,
    alignSelf: 'center',
    borderRadius: 20,
  },
  variantHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: ticketColors.ruleSoft,
  },
  variantActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  actionLink: {
    fontFamily: ticketFonts.sans,
    fontSize: 13,
    color: ticketColors.ink,
    textDecorationLine: 'underline',
  },
  actionSep: {
    color: ticketColors.ink3,
    fontSize: 13,
  },
  selectedCount: {
    marginLeft: spacing.sm,
    fontFamily: ticketFonts.sans,
    fontSize: 12,
    color: ticketColors.ink3,
  },
  confirmBtn: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: 8,
    backgroundColor: ticketColors.ink,
  },
  confirmBtnDisabled: {
    opacity: 0.4,
  },
  confirmBtnText: {
    fontFamily: ticketFonts.sans,
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.paper,
  },
  variantList: {
    maxHeight: 400,
  },
  variantRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: ticketColors.ruleSoft,
    gap: spacing.sm,
  },
  variantRowSelected: {
    backgroundColor: ticketColors.counter,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: ticketColors.ink,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxText: {
    fontSize: 12,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  variantInfo: {
    flex: 1,
  },
  variantLabel: {
    fontFamily: ticketFonts.sans,
    fontSize: 14,
    color: ticketColors.ink,
  },
  variantDetail: {
    fontFamily: ticketFonts.sans,
    fontSize: 12,
    color: ticketColors.ink3,
    marginTop: 2,
  },
  emptyText: {
    padding: spacing.md,
    fontFamily: ticketFonts.sans,
    fontSize: 13,
    color: ticketColors.ink3,
    textAlign: 'center',
  },
});
